import { useState } from "react";
import { AnimatePresence } from "framer-motion";
import CalenderEventCard from "../components/ui/Card/CalenderEventCard";
import AcademicCalenderFilter from "./AcademicCalenderFilter";
import ImageHeading from "../components/ui/ImageHeading";
import useAxiosPublic from "../hooks/useAxiosPublic";
import { useQuery } from "@tanstack/react-query";
import LoadingPage from "../components/Layout/LoadingPage";

const normalizeEventType = (calendarType = "") => {
  const type = calendarType.toLowerCase();

  if (type === "holidays" || type === "holiday") return "holiday";
  if (type === "payments" || type === "financial") return "financial";
  if (type === "exams" || type === "exam") return "exam";
  if (type === "academic") return "academic";

  return "others";
};

const toValidDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const AcademicCalender = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentFilter, setCurrentFilter] = useState("all");

  const axiosPublic = useAxiosPublic();

  const {
    data: academicCalendarData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["academicCalendar"],
    queryFn: async () => {
      const res = await axiosPublic.get("/academics/academic-calendar");
      return res?.data?.data;
    },
  });

  // Transform API data to match the expected format
  const transformedEvents =
    academicCalendarData?.academic_calendar?.map((event) => ({
      id: event.id,
      title: event.title,
      description: event.description,
      date: {
        start: toValidDate(event.start_date),
        end: toValidDate(event.end_date),
      },
      type: normalizeEventType(event.calendar_type),
      semester: event.semester,
      year: event.year,
      url: event.url,
    })) || [];

  const filteredEvents = transformedEvents.filter((event) => {
    const matchesSearch =
      event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      event.description?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesFilter = currentFilter === "all" || event.type === currentFilter;

    return matchesSearch && matchesFilter;
  });

  const getEventDate = (event) => {
    return event?.date?.start || event?.date?.end || new Date(0);
  };

  if (isLoading) {
    return <LoadingPage />;
  }

  if (error) {
    return (
      <div className="text-center py-20 text-red-500">Error loading data</div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <ImageHeading
        heading={academicCalendarData?.hero?.title}
        imageUrl={academicCalendarData?.hero?.media_url}
      />
      <AcademicCalenderFilter
        subtitle={academicCalendarData?.hero?.sub_title}
        onSearch={setSearchTerm}
        onFilter={setCurrentFilter}
        currentFilter={currentFilter}
      />

      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="space-y-4">
          <AnimatePresence>
            {filteredEvents
              .sort((a, b) => getEventDate(a) - getEventDate(b))
              .map((event) => (
                <CalenderEventCard key={event.id} event={event} />
              ))}
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
};

export default AcademicCalender;
